import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
  CommandDialog, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList,
} from "./ui/command";
import api from "../lib/api";
import { usePermissions } from "../contexts/PermissionsContext";
import {
  LayoutDashboard, Calendar, Users, ShoppingCart, BarChart3,
  UserCog, Settings, Megaphone, Shield, User, Scissors, MapPin,
  ChevronRight, Sparkles, Loader2, CornerDownLeft,
} from "lucide-react";

// Module nav index (kept in sync with Sidebar.js)
const MODULES = [
  { to: "/dashboard", label: "Dashboard", group: "General", icon: LayoutDashboard, module: "dashboard" },
  { to: "/calendar", label: "Calendar", group: "Schedule", icon: Calendar, module: "calendar" },
  { to: "/waitlist", label: "Waitlist", group: "Schedule", icon: Calendar, module: "waitlist" },
  { to: "/clients", label: "All Clients", group: "Clients", icon: Users, module: "clients" },
  { to: "/client-scores", label: "Client Scores", group: "Clients", icon: Users, module: "client_scores" },
  { to: "/score-alerts", label: "Score Alerts", group: "Clients", icon: Users, module: "score_alerts" },
  { to: "/noshow-risk", label: "No-Show Risk", group: "Clients", icon: Users, module: "noshow_risk" },
  { to: "/staff", label: "Staff", group: "Team", icon: UserCog, module: "staff" },
  { to: "/team-hub", label: "Team Hub", group: "Team", icon: UserCog, module: "team_hub" },
  { to: "/team-performance", label: "Team Performance", group: "Team", icon: UserCog, module: "team_performance" },
  { to: "/training", label: "Training", group: "Team", icon: UserCog, module: "training" },
  { to: "/timeclock", label: "Time Clock", group: "Team", icon: UserCog, module: "timeclock" },
  { to: "/shift-briefing", label: "Shift Briefing", group: "Team", icon: UserCog, module: "shift_briefing" },
  { to: "/payroll", label: "Payroll", group: "Team", icon: UserCog, module: "payroll" },
  { to: "/pos", label: "Checkout (POS)", group: "Sales", icon: ShoppingCart, module: "pos" },
  { to: "/memberships", label: "Memberships", group: "Sales", icon: ShoppingCart, module: "memberships" },
  { to: "/loyalty", label: "Loyalty", group: "Sales", icon: ShoppingCart, module: "loyalty" },
  { to: "/referrals", label: "Referrals", group: "Sales", icon: ShoppingCart, module: "referrals" },
  { to: "/gift-cards", label: "Gift Cards", group: "Sales", icon: ShoppingCart, module: "gift_cards" },
  { to: "/inventory", label: "Inventory", group: "Sales", icon: ShoppingCart, module: "inventory" },
  { to: "/marketing", label: "Campaigns", group: "Marketing", icon: Megaphone, module: "marketing" },
  { to: "/rebook", label: "Due for Rebook", group: "Marketing", icon: Megaphone, module: "rebook" },
  { to: "/automations", label: "Automations", group: "Marketing", icon: Megaphone, module: "automations" },
  { to: "/reviews", label: "Reviews", group: "Marketing", icon: Megaphone, module: "reviews" },
  { to: "/forms", label: "Forms", group: "Marketing", icon: Megaphone, module: "forms" },
  { to: "/reports", label: "Reports", group: "Insights", icon: BarChart3, module: "reports" },
  { to: "/executive", label: "Executive Dashboard", group: "Insights", icon: BarChart3, module: "executive" },
  { to: "/location-pulse", label: "Location Pulse", group: "Insights", icon: BarChart3, module: "location_pulse" },
  { to: "/command-center", label: "Command Center", group: "Insights", icon: BarChart3, module: "command_center" },
  { to: "/settings", label: "Settings", group: "Admin", icon: Settings, module: "settings" },
  { to: "/integrations", label: "Integrations", group: "Admin", icon: Shield, module: "integrations" },
  { to: "/webhooks", label: "Webhooks", group: "Admin", icon: Shield, module: "webhooks" },
  { to: "/white-label", label: "White Label", group: "Admin", icon: Shield, module: "white_label" },
];

export default function CommandPalette({ open, onOpenChange }) {
  const navigate = useNavigate();
  const { canView } = usePermissions();
  const [q, setQ] = useState("");
  const [clients, setClients] = useState([]);
  const [staff, setStaff] = useState([]);
  const [services, setServices] = useState([]);
  // AI state
  const [aiQuestion, setAiQuestion] = useState(null); // the question currently being asked
  const [aiAnswer, setAiAnswer] = useState("");
  const [aiLoading, setAiLoading] = useState(false);
  const [aiError, setAiError] = useState("");

  // Load searchable data once when palette opens
  useEffect(() => {
    if (!open) return;
    (async () => {
      try {
        const [c, s, sv] = await Promise.all([
          api.get("/clients", { params: { limit: 500 } }),
          api.get("/staff"),
          api.get("/services"),
        ]);
        // /clients returns { clients: [...], total }
        setClients(Array.isArray(c.data) ? c.data : (c.data?.clients || []));
        setStaff(Array.isArray(s.data) ? s.data : (s.data?.staff || s.data?.users || []));
        setServices(Array.isArray(sv.data) ? sv.data : (sv.data?.services || []));
      } catch {
        /* fail silently - palette still useful for modules */
      }
    })();
  }, [open]);

  // Reset query when closed
  useEffect(() => {
    if (!open) {
      setQ("");
      setAiQuestion(null);
      setAiAnswer("");
      setAiError("");
      setAiLoading(false);
    }
  }, [open]);

  const askAi = useCallback(async (question) => {
    if (!question || question.trim().length < 3) return;
    setAiQuestion(question);
    setAiAnswer("");
    setAiError("");
    setAiLoading(true);
    try {
      const res = await api.post("/ai/ask", { question });
      setAiAnswer(res.data?.answer || "");
    } catch (err) {
      setAiError(err.response?.data?.detail || "AI is taking a break. Try again in a moment.");
    } finally {
      setAiLoading(false);
    }
  }, []);

  const go = useCallback((path) => {
    onOpenChange(false);
    navigate(path);
  }, [navigate, onOpenChange]);

  const visibleModules = MODULES.filter(m => canView(m.module));

  const normalized = q.trim().toLowerCase();
  const matchClient = (c) => {
    if (!normalized) return false;
    const name = `${c.first_name || ""} ${c.last_name || ""}`.toLowerCase();
    return name.includes(normalized) || (c.email || "").toLowerCase().includes(normalized) || (c.phone || "").includes(normalized);
  };
  const matchStaff = (s) => {
    if (!normalized) return false;
    return (s.name || "").toLowerCase().includes(normalized) || (s.email || "").toLowerCase().includes(normalized);
  };
  const matchService = (s) => {
    if (!normalized) return false;
    return (s.name || "").toLowerCase().includes(normalized) || (s.category || "").toLowerCase().includes(normalized);
  };

  const clientMatches = clients.filter(matchClient).slice(0, 6);
  const staffMatches = staff.filter(matchStaff).slice(0, 5);
  const serviceMatches = services.filter(matchService).slice(0, 5);

  // Group modules by their "group" label
  const modulesByGroup = visibleModules.reduce((acc, m) => {
    (acc[m.group] = acc[m.group] || []).push(m);
    return acc;
  }, {});

  const hasAnyStaticMatch = clientMatches.length || staffMatches.length || serviceMatches.length;
  const showAskAi = normalized.length >= 3; // always offer ai when user typed meaningful text

  return (
    <CommandDialog open={open} onOpenChange={onOpenChange}>
      <CommandInput
        placeholder="Ask AI or search modules, clients, staff, services…"
        value={q}
        onValueChange={(v) => { setQ(v); if (aiQuestion && v !== aiQuestion) { setAiQuestion(null); setAiAnswer(""); setAiError(""); } }}
        data-testid="command-palette-input"
        onKeyDown={(e) => {
          // Cmd/Ctrl+Enter triggers AI directly (shortcut for power users)
          if ((e.metaKey || e.ctrlKey) && e.key === "Enter" && normalized.length >= 3) {
            e.preventDefault();
            askAi(q);
          }
        }}
      />
      <CommandList className="max-h-[520px]">
        {/* AI answer card (shows after a question is asked) */}
        {aiQuestion && (
          <div className="px-3 pt-3" data-testid="ai-answer-card">
            <div className="rounded-2xl border border-violet-200 bg-gradient-to-br from-violet-50 via-white to-rose-50 p-4">
              <div className="flex items-start gap-2.5">
                <div className="w-7 h-7 rounded-lg bg-gradient-to-br from-rose-500 via-violet-500 to-teal-500 flex items-center justify-center flex-shrink-0">
                  <Sparkles className="w-3.5 h-3.5 text-white" strokeWidth={2.5} />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-[11px] font-bold uppercase tracking-widest text-violet-600 font-['Manrope']">Ask SalonFlow</p>
                  <p className="text-[13px] font-medium text-slate-700 mt-0.5 italic">"{aiQuestion}"</p>
                  <div className="mt-2.5 min-h-[40px]">
                    {aiLoading && (
                      <div className="flex items-center gap-2 text-slate-500 text-sm">
                        <Loader2 className="w-3.5 h-3.5 animate-spin" />
                        <span>Looking at your numbers…</span>
                      </div>
                    )}
                    {aiError && (
                      <p className="text-sm text-rose-600" data-testid="ai-error">{aiError}</p>
                    )}
                    {aiAnswer && !aiLoading && (
                      <p className="text-[14px] text-slate-900 leading-relaxed font-['Manrope']" data-testid="ai-answer-text">{aiAnswer}</p>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Ask AI action (when typing + not already asking) */}
        {showAskAi && !aiQuestion && (
          <CommandGroup heading="AI Assistant">
            <CommandItem
              value={`ai-ask-${q}`}
              onSelect={() => askAi(q)}
              data-testid="cmd-ask-ai"
              className="gap-3 border border-violet-200 bg-gradient-to-r from-violet-50 to-rose-50 rounded-xl">
              <div className="w-7 h-7 rounded-lg bg-gradient-to-br from-rose-500 via-violet-500 to-teal-500 flex items-center justify-center">
                <Sparkles className="w-3.5 h-3.5 text-white" strokeWidth={2.5} />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold text-slate-900">Ask AI: <span className="text-violet-700 italic">"{q}"</span></p>
                <p className="text-[11px] text-slate-500 font-['Manrope']">Answers from your live salon data</p>
              </div>
              <span className="inline-flex items-center gap-1 text-[10px] text-slate-400 font-semibold uppercase tracking-wider">
                <CornerDownLeft className="w-3 h-3" /> Enter
              </span>
            </CommandItem>
          </CommandGroup>
        )}

        <CommandEmpty>{aiQuestion ? "Type a new question or browse below." : "No results found."}</CommandEmpty>

        {clientMatches.length > 0 && (
          <CommandGroup heading="Clients">
            {clientMatches.map(c => (
              <CommandItem key={c.client_id} value={`client-${c.client_id}-${c.first_name}-${c.last_name}-${c.email}`}
                onSelect={() => go(`/clients/${c.client_id}`)}
                data-testid={`cmd-client-${c.client_id}`}
                className="gap-3">
                <div className="w-7 h-7 rounded-full bg-gradient-to-br from-rose-100 to-violet-100 flex items-center justify-center text-[11px] font-semibold text-rose-600">
                  {(c.first_name || "?")[0]}{(c.last_name || "")[0]}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-slate-900 truncate">{c.first_name} {c.last_name}</p>
                  <p className="text-xs text-slate-400 truncate">{c.email}</p>
                </div>
                <span className="text-[10px] font-semibold uppercase tracking-wider text-slate-300">Client</span>
              </CommandItem>
            ))}
          </CommandGroup>
        )}

        {staffMatches.length > 0 && (
          <CommandGroup heading="Staff">
            {staffMatches.map(s => (
              <CommandItem key={s.user_id} value={`staff-${s.user_id}-${s.name}-${s.email}`}
                onSelect={() => go(`/staff?focus=${s.user_id}`)}
                data-testid={`cmd-staff-${s.user_id}`}
                className="gap-3">
                <div className="w-7 h-7 rounded-full flex items-center justify-center text-white text-[11px] font-semibold" style={{ backgroundColor: s.color || "#64748B" }}>
                  {(s.name || "?")[0]}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-slate-900 truncate">{s.name}</p>
                  <p className="text-xs text-slate-400 truncate">{s.title || s.role}</p>
                </div>
                <span className="text-[10px] font-semibold uppercase tracking-wider text-slate-300">Staff</span>
              </CommandItem>
            ))}
          </CommandGroup>
        )}

        {serviceMatches.length > 0 && (
          <CommandGroup heading="Services">
            {serviceMatches.map(s => (
              <CommandItem key={s.service_id} value={`service-${s.service_id}-${s.name}-${s.category}`}
                onSelect={() => go(`/settings?tab=services&focus=${s.service_id}`)}
                data-testid={`cmd-service-${s.service_id}`}
                className="gap-3">
                <div className="w-7 h-7 rounded-lg flex items-center justify-center" style={{ backgroundColor: `${s.color || "#F43F5E"}20` }}>
                  <Scissors className="w-3.5 h-3.5" style={{ color: s.color || "#F43F5E" }} strokeWidth={2} />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-slate-900 truncate">{s.name}</p>
                  <p className="text-xs text-slate-400 truncate">{s.category} · {s.duration}min · ${s.price}</p>
                </div>
                <span className="text-[10px] font-semibold uppercase tracking-wider text-slate-300">Service</span>
              </CommandItem>
            ))}
          </CommandGroup>
        )}

        {Object.entries(modulesByGroup).map(([group, items]) => (
          <CommandGroup key={group} heading={group}>
            {items.map(m => (
              <CommandItem key={m.to} value={`${group}-${m.label}`}
                onSelect={() => go(m.to)}
                data-testid={`cmd-module-${m.to.replace(/\//g, "")}`}
                className="gap-3">
                <div className="w-7 h-7 rounded-lg bg-slate-50 flex items-center justify-center">
                  <m.icon className="w-3.5 h-3.5 text-slate-500" strokeWidth={1.8} />
                </div>
                <span className="text-sm font-medium text-slate-800 flex-1">{m.label}</span>
                <ChevronRight className="w-3.5 h-3.5 text-slate-300" />
              </CommandItem>
            ))}
          </CommandGroup>
        ))}
      </CommandList>
    </CommandDialog>
  );
}
